import { OwnedObjectType } from '../Inventory/OwnedObjects';
import { ModalBase } from './Base';
import { extractSudoCardFields } from '../DisplayObject';
import { Button } from '../Base/Button';
import useTraderOnChainPoints, {
  TraderOnChainPoints,
} from '../../../hooks/useTraderOnChainPoints';
import { executeApiCall, getCoinObjects, priceToString } from '../../utils';
import { useSuiClient } from '@mysten/dapp-kit';
import { useKioskClient } from '../../../context/KioskClientContext';
import { useEssential } from '../../App';
import { DataAPI } from 'sudo-ts-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useSudoCardPoints } from '../../../hooks/useSudoCardPoints';
import { KioskOwnerCap } from '@mysten/kiosk';
import { ClaimPointsConfirmation } from './ClaimPointsConfirmation';
import { extractPointsAmount } from '../../../utils/utils';
import { formatNumber } from '../../utils';
import { Loading } from '../Base/Loading';
import { useSudoCardBalance } from '../../../hooks/useSudoCardBalance';
import { ITokenBalance } from '../../../hooks/useAccountData';

export interface SudoCardDetailsProps {
  item: OwnedObjectType;
  closeModal: () => void;
  refreshKiosk: () => Promise<any>;
  kioskCap: KioskOwnerCap;
}

export function SudoCardDetails({
  item,
  kioskCap,
  closeModal,
  refreshKiosk,
}: SudoCardDetailsProps) {
  const suiClient = useSuiClient();
  const kioskClient = useKioskClient();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isClaiming, setIsClaiming] = useState<any>({});
  const [invalid, setInvalid] = useState('');
  const [confirmationModalItem, setConfirmationModalItem] =
    useState<OwnedObjectType | null>(null);

  const { wallet, network, consts, accountData, refreshAccount } =
    useEssential();

  const {
    data: coinBalance,
    isLoading: isCoinBalanceLoading,
    refetch: refetchCoinBalance,
  } = useSudoCardBalance({ consts: consts, address: item.objectId });

  const {
    data: pointsObjects,
    isLoading,
    refetch: refetchPointsObjects,
  } = useSudoCardPoints({ consts: consts, address: accountData.address });

  const {
    data: pointsObjectsOnSudoCard,
    isLoading: isLoadingOnSudoCard,
    refetch: refetchPointsObjectsOnSudoCard,
  } = useSudoCardPoints({ consts: consts, address: item.objectId });

  const {
    data: onChainPoints,
    isLoading: isOnChainPointsLoading,
    refetch: refetchOnChainPoints,
  } = useTraderOnChainPoints(accountData.address);

  useEffect(() => {
    if (!accountData.address) {
      setInvalid('N/A');
      return;
    }

    setInvalid('');
  }, [accountData.address]);

  const dataAPI = new DataAPI(network, suiClient);
  const { number, tier, level, points } = extractSudoCardFields(item.display);

  const claimablePointsObjects = useMemo(() => {
    if (!pointsObjects || !onChainPoints) return [];

    const mappedOnChainPoints = onChainPoints.map(point => {
      point.claimable = false;
      pointsObjects.forEach(pointObject => {
        if (pointObject.objectId === point.objectId) {
          point.claimable = true;
          return;
        }
      });
      return point;
    });

    const claimablePoints = mappedOnChainPoints.filter(
      point => point.claimable,
    );

    return claimablePoints.sort((a, b) => {
      if (a === null || b === null) return 0;
      return a.endTimeMs < b.endTimeMs ? 1 : -1;
    });
  }, [pointsObjects, onChainPoints]);

  const claimablePointsAmount = useMemo(() => {
    return claimablePointsObjects.reduce((acc, point) => {
      return acc + point.amount;
    }, 0);
  }, [claimablePointsObjects]);

  const claimablePointsOnSudoCardAmount = useMemo(() => {
    return pointsObjectsOnSudoCard
      ? pointsObjectsOnSudoCard.reduce((acc, point) => {
          let amount = extractPointsAmount(point);
          amount = amount ? amount : 0;
          return acc + amount;
        }, 0)
      : 0;
  }, [pointsObjectsOnSudoCard]);

  const pointsNeededForLevelUp = (level: number) => {
    return 1000 + 2000 * level * level;
  };

  const transferAndReceivePoints = (
    card: string,
    points: TraderOnChainPoints[],
  ) => {
    // Transfer points to the card
    executeApiCall(
      async api => {
        return api.transferPoints(
          points.map(point => point.objectId),
          card,
        );
      },
      res => {
        receivePoints(
          card,
          points.map(point => point.objectId),
        );
        const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        console.error(e);
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount],
    );
  };

  const levelUp = (card: string) => {
    executeApiCall(
      async api => {
        return api.levelUp(kioskClient, kioskCap, card);
      },
      res => {
        const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        console.error(e);
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount, refreshKiosk],
    ).finally(() => setIsSubmitLoading(false));
  };

  const receivePoints = (card: string, points: string[]) => {
    // Receive points on the card
    executeApiCall(
      async api => {
        return api.receivePoints(kioskClient, kioskCap!, card, points);
      },
      res => {
        const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;

        dataAPI.updateOnChainPoints(points, res?.digest);
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        console.error(e);
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [
        refreshAccount,
        refetchPointsObjectsOnSudoCard,
        refetchOnChainPoints,
        refreshKiosk,
      ],
    ).finally(() => setIsSubmitLoading(false));
  };

  const claimTokenFromSCard = (token: ITokenBalance) => {
    const card = item.objectId;

    if (!card || !kioskCap) {
      return;
    }

    setIsClaiming({ [token.symbol.toLowerCase()]: true });
    executeApiCall(
      async api => {
        const coins = await getCoinObjects(
          card,
          consts.coins[token.symbol.toLowerCase()].module,
          network,
        );
        const coinObjects = coins.map(e => e.objectId);
        if (coinObjects.length === 0) {
          throw new Error('No claimable tokens found.');
        }
        return api.claimTokenFromSCard(
          token.symbol,
          coinObjects,
          kioskClient,
          kioskCap!,
          card,
        );
      },
      res => {
        const txnUrl = `https://suivision.xyz/txblock/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refetchCoinBalance],
    ).finally(() => setIsClaiming({ [token.symbol.toLowerCase()]: false }));
  };

  const renderSCardAccountBalance = () => {
    if (!coinBalance) return null;

    return (
      <div className="flex flex-col gap-2 text-white">
        <div> Card Balance </div>
        {coinBalance.balance.length > 0 ? (
          coinBalance.balance.map(balance => {
            return (
              <div
                className="flex flex-row justify-between"
                key={balance.symbol}
              >
                {balance.symbol && (
                  <>
                    <div className="text-sm align-center px-2">
                      {' '}
                      {balance.symbol.toUpperCase()}{' '}
                    </div>
                    <div className="flex flex-row">
                      <div className="text-sm align-center px-2">
                        {' '}
                        {priceToString(balance.balance)}{' '}
                      </div>
                      <div className="ml-4 min-w-[51px]">
                        <button
                          className={
                            'ease-in-out duration-300 disabled:opacity-30 py-0.5 px-2 bg-green w-full max-h-[24px]'
                          }
                          onClick={() => claimTokenFromSCard(balance)}
                        >
                          {isClaiming[balance.symbol.toLowerCase()] ? (
                            <span className="text-12 align-center">
                              {' '}
                              Claiming...{' '}
                            </span>
                          ) : (
                            <span className="text-12 align-center">
                              {' '}
                              Claim{' '}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div>{'No Token Found.'}</div>
        )}
      </div>
    );
  };

  if (
    isLoading ||
    isLoadingOnSudoCard ||
    isOnChainPointsLoading ||
    isCoinBalanceLoading
  ) {
    return (
      <ModalBase
        isOpen
        size={'xl'}
        closeModal={closeModal}
        title={item.display.name}
      >
        <Loading></Loading>
      </ModalBase>
    );
  }

  return (
    <ModalBase
      isOpen
      size={'xl'}
      closeModal={closeModal}
      title={item.display.name}
    >
      {/* Only shows item with an image_url to make it easier to understand the flows. */}

      <div
        className={`section-container py-5 sm:py-10 sm:px-14 px-5 relative border-primary border-1`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 w-full">
          <div className="relative cursor-pointer transition-transform hover:scale-[101%] overflow-hidden">
            <img
              src={item.display.image_url}
              className="overflow-hidden rounded-xl w-full aspect-square object-cover scale-[1.01]"
              alt="The display of the object"
            ></img>
          </div>

          <div className="flex flex-col gap-4 text-white">
            {item.display.name && (
              <h1 className="text-clip text-xl text-left overflow-hidden flex items-center justify-between">
                <span>
                  {item.display.name} #{number}
                </span>
                <a
                  href={`https://suivision.xyz/object/${item.objectId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View on Explorer"
                >
                  <svg
                    className="!w-6 !h-6 fill-white hover:fill-green transition-all duration-700"
                    stroke="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 24 24"
                    height="200px"
                    width="200px"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="none" d="M0 0h24v24H0V0z"></path>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5.5-2.5 7.51-3.49L17.5 6.5 9.99 9.99 6.5 17.5zm5.5-6.6c.61 0 1.1.49 1.1 1.1s-.49 1.1-1.1 1.1-1.1-.49-1.1-1.1.49-1.1 1.1-1.1z"></path>
                  </svg>
                </a>
              </h1>
            )}

            {item.display.description && (
              <p className="text-sm opacity-80 text-clip overflow-hidden">
                {item.display.description}
              </p>
            )}

            <p className="text-sm text-left break-words py-1">
              Object ID: {item.objectId}
            </p>

            <p className="text-small text-left break-words py-1">
              Tier: {tier}
            </p>

            {level && points && (
              <div className="text-small text-left break-words py-1 flex items-center justify-between">
                <div>Level: {level}</div>
                <Button
                  loading={false}
                  disabled={!!invalid || points < pointsNeededForLevelUp(level)}
                  className="app-tab-button app-loader-button bg-green font-medium md:col-span-2 !px-2 !py-1 text-sm"
                  onClick={() => {
                    levelUp(item.objectId);
                  }}
                >
                  LEVEL UP
                </Button>
              </div>
            )}

            {points && (
              <div className="text-small text-left break-words py-1 flex items-center justify-between">
                <div>Points: {points}</div>
              </div>
            )}
            {claimablePointsAmount > 0 && (
              <div className="text-small text-left break-words py-1 flex items-center justify-between">
                <div>
                  {' '}
                  Claimable Points: {formatNumber(claimablePointsAmount)}
                </div>
                <Button
                  loading={isSubmitLoading}
                  disabled={!!invalid || claimablePointsAmount === 0}
                  className="app-tab-button app-loader-button bg-green font-medium md:col-span-2 !px-2 !py-1 text-sm"
                  onClick={() => {
                    transferAndReceivePoints(
                      item.objectId,
                      claimablePointsObjects,
                    );
                  }}
                >
                  CLAIM
                </Button>
              </div>
            )}
            {pointsObjectsOnSudoCard && claimablePointsOnSudoCardAmount > 0 && (
              <div className="text-small text-left break-words py-1 flex items-center justify-between">
                <div>
                  {' '}
                  Receivable Points:{' '}
                  {formatNumber(claimablePointsOnSudoCardAmount)}
                </div>
                <Button
                  loading={isSubmitLoading}
                  disabled={!!invalid || claimablePointsOnSudoCardAmount === 0}
                  className="app-tab-button app-loader-button bg-green font-medium md:col-span-2 !px-2 !py-1 text-sm"
                  onClick={() => {
                    receivePoints(
                      item.objectId,
                      pointsObjectsOnSudoCard.map(point => point.objectId),
                    );
                  }}
                >
                  RECEIVE
                </Button>
              </div>
            )}
            {coinBalance && renderSCardAccountBalance()}
          </div>
        </div>
      </div>
    </ModalBase>
  );
}
